import {
  DatePicker,
  Divider,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const rangePresets = [
  {
    label: "Hôm nay",
    value: [dayjs(), dayjs()],
  },
  {
    label: "Hôm qua",
    value: [dayjs().add(-1, "d"), dayjs().add(-1, "d")],
  },
  {
    label: "Tuần này",
    value: [dayjs().startOf("week"), dayjs()],
  },
  {
    label: "Tuần trước",
    value: [
      dayjs().subtract(1, "week").startOf("week"),
      dayjs().subtract(1, "week").endOf("week"),
    ],
  },
  {
    label: "Tháng này",
    value: [dayjs().startOf("month"), dayjs()],
  },
  {
    label: "Tháng trước",
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: "Tất cả",
    value: [dayjs("01/07/2023", "DD/MM/YYYY"), dayjs()],
  },
];
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import api from "../api";
import { fetchRegions } from "features/region/slice";

const { Title } = Typography;

export default function EmailStats() {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { regions } = useSelector((state) => state.region);

  const [emailData, setEmailData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [searchEmail, setSearchEmail] = useState("");
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);

  // Fetch regions nếu chưa có
  useEffect(() => {
    if (regions.length === 0) {
      dispatch(fetchRegions());
    }
  }, [dispatch, regions.length]);

  // Lấy region hiện tại
  useEffect(() => {
    if (userInfo) {
      let currentRegionId;
      if (userInfo.role === "super_admin") {
        currentRegionId = localStorage.getItem("region");
      } else {
        currentRegionId = userInfo.region;
      }
      if (currentRegionId) {
        setSelectedRegion(currentRegionId);
      }
    }
  }, [userInfo]);

  // Fetch data khi component mount hoặc filter thay đổi
  useEffect(() => {
    if (selectedRegion) {
      fetchEmailData();
    }
  }, [selectedRegion, pagination.current, pagination.pageSize, dateRange]);

  const fetchEmailData = async () => {
    setIsLoading(true);
    try {
      const params = {
        current: pagination.current,
        pageSize: pagination.pageSize,
        region: selectedRegion,
      };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.from = dateRange[0].format("YYYY-MM-DD");
        params.to = dateRange[1].format("YYYY-MM-DD");
      }
      if (searchEmail && searchEmail.trim()) {
        params.email = searchEmail.trim();
      }
      const data = await api.getEmailStats(params);
      if (data.error) {
        toast.error(data.error.msg || "Lỗi khi tải dữ liệu email tracking");
        return;
      }
      setEmailData(data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: data.meta?.total || 0,
      }));
    } catch (error) {
      console.error(error);
      toast.error("Có lỗi xảy ra khi tải dữ liệu");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegionChange = (value) => {
    setSelectedRegion(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchEmailData();
  };

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const columns = [
    {
      title: "STT",
      align: "center",
      width: "5%",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Nhà hàng",
      dataIndex: "restaurant",
      width: "25%",
      render: (restaurant) => {
        if (!restaurant) return "N/A";
        return (
          <Space>
            <span>{restaurant.name || "N/A"}</span>
            <Typography.Text
              copyable={{
                text: restaurant._id,
                tooltips: ["Sao chép ID", "Đã sao chép!"],
              }}
              style={{ fontSize: 12 }}
            />
          </Space>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "20%",
      render: (email) => email || "N/A",
    },
    {
      title: "OTP",
      dataIndex: "otp",
      width: "10%",
      align: "center",
      render: (otp) => otp || "N/A",
    },
    {
      title: "Thời gian gửi",
      dataIndex: "createdAt",
      width: "15%",
      render: (createdAt) => {
        if (!createdAt) return "N/A";
        return dayjs(createdAt).format("DD/MM/YYYY HH:mm:ss");
      },
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle">
        <Title level={2}>Thống kê Email Tracking</Title>
        <Space size="middle">
          <RangePicker
            presets={rangePresets}
            value={dateRange}
            onChange={handleDateRangeChange}
            format="DD/MM/YYYY"
            style={{ width: 250 }}
          />
          <Select
            placeholder="Chọn region"
            value={selectedRegion}
            onChange={handleRegionChange}
            style={{ width: 200 }}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={regions.map((region) => ({
              value: region._id,
              label: region.name,
            }))}
          />
          <Input.Search
            placeholder="Tìm kiếm theo email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onSearch={handleSearch}
            enterButton
            allowClear
            style={{ width: 250 }}
          />
        </Space>
      </Row>
      <Divider />
      <Table
        bordered
        loading={isLoading}
        columns={columns}
        dataSource={emailData}
        rowKey={(record) => record._id || Math.random()}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} mục`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTableChange}
        locale={{
          emptyText: <Empty description="Không có dữ liệu" />,
        }}
      />
    </>
  );
}
